import { Download } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '~/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '~/components/ui/tooltip';
import { cn } from '~/utils/cn';

import { useExportTenantContext } from '../hooks/use-export-tenant-context';
import { useExportStore } from '../store/export.store';
import type { ExportConnector } from '../types/export.types';
import { isTerminalExportStatus } from '../types/export.types';

import { ExportHistorySheet } from './export-history-sheet';
import { ExportSetupSheet } from './export-setup-sheet';

interface ExportDownloadButtonProps {
  /** The enrichment run's workflow slug. Null/undefined while it resolves — the button disables. */
  sourceWorkflowId: string | null | undefined;
  /** Human context threaded into the estimate dialog + progress toast. */
  label: string;
  connector: ExportConnector;
  variant?: 'default' | 'outline';
  size?: 'default' | 'sm';
  /** Extra classes for the trigger button — used to match a neighbouring button's style. */
  className?: string;
  /** Extra classes for the label text only. Lets a cramped toolbar collapse the
   *  button to icon-only (e.g. `max-[1180px]:sr-only`) — `sr-only` rather than
   *  `hidden` so the button keeps its accessible name at every width. */
  labelClassName?: string;
  /** Render as an icon-only button with the label surfaced as a hover tooltip.
   *  For cramped headers/toolbars (e.g. next to other icon buttons) where a full
   *  labelled button doesn't fit. */
  iconOnly?: boolean;
  /**
   * The run's enriched attribute keys, when this call site knows them — the
   * column picker defaults to them instead of to every connector column.
   * Only the batch-detail page holds the run header; everywhere else omits it
   * and keeps the all-columns default.
   */
  batchAttributeKeys?: readonly string[];
  /** `data-testid` for the trigger button. */
  testId?: string;
}

/**
 * Reusable "Download / Export CSV" entry point dropped at every export trigger
 * site. Opens the export setup drawer (estimate, template, preview, format); a
 * secondary button opens the per-run export history. Disables (with a reason tooltip) while the source
 * workflow id or tenant context is unresolved, or while an export for this run
 * is already in flight.
 */
export function ExportDownloadButton({
  sourceWorkflowId,
  label,
  connector,
  variant = 'outline',
  size = 'sm',
  className,
  labelClassName,
  iconOnly = false,
  batchAttributeKeys,
  testId = 'export-download-button',
}: ExportDownloadButtonProps) {
  const { t } = useTranslation('exports');
  const tenantContext = useExportTenantContext();
  const [setupOpen, setSetupOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);

  // Subscribe to sessions so the disabled state updates live as exports start /
  // finish. Only non-terminal sessions for THIS run block a fresh trigger.
  const sessions = useExportStore((s) => s.sessions);
  const hasActive = useMemo(
    () =>
      !!sourceWorkflowId &&
      Object.values(sessions).some(
        (s) =>
          s.sourceWorkflowId === sourceWorkflowId &&
          (s.latest == null || !isTerminalExportStatus(s.latest.status)),
      ),
    [sessions, sourceWorkflowId],
  );

  const missingContext = !sourceWorkflowId || !tenantContext;
  const disabled = missingContext || hasActive;
  const disabledReason = missingContext
    ? t('button.unavailable')
    : hasActive
      ? t('button.inProgress')
      : '';

  const trigger = iconOnly ? (
    <Button
      type="button"
      variant={variant}
      size="icon"
      disabled={disabled}
      aria-label={t('button.label')}
      data-testid={testId}
      onClick={() => {
        setSetupOpen(true);
      }}
      className={cn(disabled ? 'cursor-not-allowed' : 'cursor-pointer', className)}
    >
      <Download aria-hidden="true" className="h-4 w-4" />
    </Button>
  ) : (
    <Button
      type="button"
      variant={variant}
      size={size}
      disabled={disabled}
      data-testid={testId}
      onClick={() => {
        setSetupOpen(true);
      }}
      className={cn(disabled ? 'cursor-not-allowed' : 'cursor-pointer', className)}
    >
      <Download aria-hidden="true" className="h-4 w-4" />
      {/* The icon/label gap lives on the label, not as `mr` on the icon: an
          `sr-only` label is position-absolute, so both the gap and this margin
          collapse and the icon-only button has no stray trailing space. */}
      <span className={cn('ml-1.5', labelClassName)}>{t('button.label')}</span>
    </Button>
  );

  // Tooltip content: the disabled reason always wins; otherwise an icon-only
  // trigger surfaces its label on hover (a labelled trigger needs no tooltip).
  const tooltipContent = disabled ? disabledReason : iconOnly ? t('button.label') : '';

  return (
    <div className="flex items-center gap-1">
      {tooltipContent ? (
        <TooltipProvider>
          <Tooltip>
            {/* span wrapper — a disabled button doesn't emit hover events itself. */}
            <TooltipTrigger asChild>
              <span>{trigger}</span>
            </TooltipTrigger>
            <TooltipContent>{tooltipContent}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ) : (
        trigger
      )}

      {sourceWorkflowId && tenantContext && (
        <ExportSetupSheet
          open={setupOpen}
          onOpenChange={setSetupOpen}
          sourceWorkflowId={sourceWorkflowId}
          tenantContext={tenantContext}
          label={label}
          connector={connector}
          {...(batchAttributeKeys ? { batchAttributeKeys } : {})}
          onOpenHistory={() => {
            setHistoryOpen(true);
          }}
        />
      )}
      {sourceWorkflowId && (
        <ExportHistorySheet
          open={historyOpen}
          onOpenChange={setHistoryOpen}
          sourceWorkflowId={sourceWorkflowId}
        />
      )}
    </div>
  );
}
